<template>
    <div class="sc-detail-body">
        <div class="sc-hist-bar">
            <div class="sc-date-nav">
                <button type="button" class="sc-btn sc-btn--icon" :aria-label="$t('이전 날')" @click="shiftDate(-1)">
                    ‹
                </button>
                <input v-model="date" type="date" :aria-label="$t('조회 날짜')" @change="load" />
                <button type="button" class="sc-btn sc-btn--icon" :aria-label="$t('다음 날')" @click="shiftDate(1)">
                    ›
                </button>
            </div>
        </div>

        <div v-if="loading" class="sc-meta">{{ $t("불러오는 중...") }}</div>
        <div v-else-if="runs.length === 0 && plannedRuns.length === 0" class="sc-empty">
            <span>{{ $t("해당 날짜의 실행 이력이 없습니다.") }}</span>
        </div>

        <div style="display: flex; flex-direction: column; gap: 12px">
            <div v-for="run in runs" :key="run.eventExecIdx" class="sc-run" :class="{ 'has-fail': run.failCnt > 0 }">
                <button
                    type="button"
                    class="sc-run-head"
                    :aria-expanded="String(expandedId === run.eventExecIdx)"
                    @click="toggleRun(run)"
                >
                    <span class="dot"></span>
                    <span class="time">{{ run.time }}</span>
                    <span class="name">{{ run.schdEventName }}</span>
                    <span class="ok">{{ $t("성공") }} {{ run.successCnt ?? 0 }}</span>
                    <span class="fail">{{ $t("실패") }} {{ run.failCnt ?? 0 }}</span>
                    <span class="sc-meta">{{ expandedId === run.eventExecIdx ? "▴" : "▾" }}</span>
                </button>
                <template v-if="expandedId === run.eventExecIdx">
                    <div v-if="logsLoading" class="sc-run-result sc-meta">{{ $t("불러오는 중...") }}</div>
                    <div v-else-if="!logs.length" class="sc-run-result sc-meta">{{ $t("처리 결과가 없습니다.") }}</div>
                    <div v-for="(log, i) in logs" :key="i" class="sc-run-result">
                        <span class="eq" :title="log.equipName">{{ log.equipName }}</span>
                        <span
                            class="sc-kind-badge"
                            :class="log.kind === 'success' ? 'is-ok' : log.kind === 'fail' ? 'is-fail' : ''"
                        >
                            {{ log.statusLabel }}
                        </span>
                        <span class="msg" :title="log.resultMsg">{{ log.resultMsg || "-" }}</span>
                        <button type="button" class="more" @click="openLogDetail(log)">{{ $t("상세") }}</button>
                    </div>
                </template>
            </div>

            <div v-for="(plan, i) in plannedRuns" :key="`p${i}`" class="sc-run is-planned">
                <div class="sc-run-head" style="cursor: default">
                    <span class="dot"></span>
                    <span class="time">{{ plan.time }}</span>
                    <span class="name">{{ plan.name }}</span>
                    <span>{{ $t("예정") }}</span>
                </div>
            </div>
        </div>

        <b-modal modal-class="sch-modal" ref="LogDetailModal" size="lg" centered @hidden="logDetail = null">
            <template #modal-header>
                <h5>{{ $t("제어 로그 상세") }}</h5>
            </template>
            <template #modal-footer>
                <ModalFooter :show-confirm="false" @cancel="$refs.LogDetailModal.hide()" />
            </template>
            <HistoryLogDetailPanel v-if="logDetail" :detail="logDetail" />
        </b-modal>
    </div>
</template>

<script>
import moment from "moment";
import backEndApi from "@src/api/backEndApi";
import ModalFooter from "../ModalFooter.vue";
import HistoryLogDetailPanel from "../../panels/HistoryLogDetailPanel.vue";
import { fetchAllEquips } from "../../utils/scheduleApi";
import { classifyResult, hhmm, activeExceptMap, selectedDayKeys, DAYS } from "../../utils/scheduleFormat";

export default {
    components: { ModalFooter, HistoryLogDetailPanel },
    props: {
        detail: { type: Object, required: true },
    },
    data() {
        return {
            date: moment().format("YYYY-MM-DD"),
            runs: [],
            loading: false,
            expandedId: null,
            logs: [],
            logsLoading: false,
            logDetail: null,
        };
    },
    computed: {
        /** 오늘 날짜를 볼 때, 아직 실행되지 않은 예정 이벤트 (이력에 없고 시각이 지나지 않은 것) */
        plannedRuns() {
            if (this.date !== moment().format("YYYY-MM-DD") || !this.appliesToday()) return [];
            const now = moment().format("HH:mm");
            const done = new Set(this.runs.map((r) => r.schdEventName));
            return (this.detail.events || [])
                .filter((e) => hhmm(e.schdHour) > now && !done.has(e.schdEventName))
                .map((e) => ({ time: hhmm(e.schdHour), name: e.schdEventName }))
                .sort((a, b) => a.time.localeCompare(b.time));
        },
    },
    watch: {
        "detail.schdCtrlIdx"() {
            this.reset();
            this.load();
        },
    },
    created() {
        this.load();
    },
    methods: {
        reset() {
            this.date = moment().format("YYYY-MM-DD");
            this.runs = [];
            this.expandedId = null;
            this.logs = [];
        },
        appliesToday() {
            const today = moment();
            const key = DAYS[(today.day() + 6) % 7].key;
            if (!selectedDayKeys(this.detail.repeatConfig).includes(key)) return false;
            const d = today.format("YYYY-MM-DD");
            if (this.detail.startDate && d < this.detail.startDate) return false;
            if (this.detail.endDate && d > this.detail.endDate) return false;
            return !activeExceptMap(this.detail.exceptConfig)[d];
        },
        shiftDate(delta) {
            this.date = moment(this.date).add(delta, "day").format("YYYY-MM-DD");
            this.load();
        },
        async load() {
            const idx = this.detail.schdCtrlIdx;
            const date = this.date;
            if (!idx || !date) return;
            this.loading = true;
            this.expandedId = null;
            this.logs = [];
            let runs = [];
            try {
                // 이력이 없는 날짜는 서버가 NoDataFound 로 응답하므로 빈 목록으로 취급한다
                const result = await backEndApi.equipControl.getSchdEventHistList(idx, date);
                if (result.status == 200 && Array.isArray(result.data)) {
                    runs = result.data.map((item) => ({
                        ...item,
                        time: moment(item.eventDate).format("HH:mm:ss"),
                    }));
                }
            } catch (e) {
                console.error(e);
            }
            // 조회 중 다른 스케줄/날짜로 바뀐 경우 결과를 버린다
            if (idx === this.detail.schdCtrlIdx && date === this.date) {
                this.runs = runs;
                this.loading = false;
            }
        },
        async toggleRun(run) {
            if (this.expandedId === run.eventExecIdx) {
                this.expandedId = null;
                return;
            }
            this.expandedId = run.eventExecIdx;
            this.logs = [];
            this.logsLoading = true;
            try {
                const [result, equips] = await Promise.all([
                    backEndApi.equipControl.getSchdEventLogList(run.eventExecIdx),
                    fetchAllEquips(),
                ]);
                if (this.expandedId !== run.eventExecIdx) return;
                if (result.status == 200 && Array.isArray(result.data)) {
                    this.logs = result.data.map((item) => {
                        const kind = classifyResult(item.resultCode);
                        const equip = equips.find((e) => e.equipIdx == item.equipIdx);
                        return {
                            ...item,
                            kind,
                            statusLabel:
                                kind === "success"
                                    ? this.$t("성공")
                                    : kind === "fail"
                                      ? this.$t("실패")
                                      : item.resultCode,
                            equipName: equip ? equip.equipName : `#${item.equipIdx}`,
                        };
                    });
                }
            } catch (e) {
                console.error(e);
            } finally {
                if (this.expandedId === run.eventExecIdx) this.logsLoading = false;
            }
        },
        async openLogDetail(log) {
            try {
                const result = await backEndApi.equipControl.getSchdEventLogDetail(log.eventExecIdx, log.logDate);
                if (result.status == 200) {
                    const detail = result.data;
                    detail.setDt = moment(detail.setDt).format("YYYY-MM-DD HH:mm:ss");
                    detail.updDt = moment(detail.updDt).format("YYYY-MM-DD HH:mm:ss");
                    detail.resultMsg = log.resultMsg;
                    this.logDetail = detail;
                    this.$refs.LogDetailModal.show();
                } else {
                    this.alertWarning(
                        `${result.data.message}`,
                        "",
                        `${typeof result.data.detail == "string" ? result.data.detail : ""}`,
                    );
                }
            } catch (e) {
                console.error(e);
            }
        },
    },
};
</script>
